import React, { createContext, useContext, useEffect, useState } from 'react';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import { SPECIES_OPTIONS } from '../utils/speciesOptions';
import compressImage from '../utils/compressImage';
import { normalizeUsPhone, normalizeZelle } from '../utils/payContacts';
import { upgradeCost, UPGRADE_TRACKS, MAX_UPGRADE_LEVEL, MAX_REBUILDS, rebuildCost, CHARTER_CLUB_COST } from '../utils/gameUpgrades';
import { charterFare } from '../utils/gameBiomes';
import { isNewRecord, speciesLabel, sizeLabel, rarityOf, JUNK_ROSTER } from '../utils/gameSpecies';
import { advanceWeekly, weeklyFor, bountyStatus, weeklyState, clubRecords } from '../utils/gameWeekly';
import { advanceQuests, QUEST_BY_KEY, questState } from '../utils/gameQuests';
import { rankDerby, previousDerby } from '../utils/gameDerby';
import { LURES, FLY_ROD } from '../utils/gameLures';
import { WARDROBE, normalizeLook } from '../utils/anglerLook';

const AuthContext = createContext(null);
const defaultProfile = { display_name: 'New angler', home_water: '', favorite_species: '', bio: '', avatar_url: '', venmo_handle: '', tour_completed_at: null };
const TOUR_STORAGE_KEY = 'fishyfriends:tour-done';

function tourSeenLocally() {
  try { return window.localStorage.getItem(TOUR_STORAGE_KEY) === '1'; } catch (storageError) { return false; }
}
const KNOWN_SPECIES = new Set(SPECIES_OPTIONS.map((option) => option.label.toLowerCase()));

const LIKE_TABLES = { personal_best: 'personal_best_likes', fish_year_catch: 'fish_year_catch_likes', tournament_entry: 'tournament_entry_likes' };
const LIKE_COLUMNS = { personal_best: 'personal_best_id', fish_year_catch: 'catch_id', tournament_entry: 'tournament_entry_id' };

function profileFromUser(user) {
  return { ...defaultProfile, display_name: user?.user_metadata?.display_name || user?.email?.split('@')[0] || defaultProfile.display_name };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(defaultProfile);
  const [personalBests, setPersonalBests] = useState([]);
  const [customSpecies, setCustomSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');

  async function loadPersonalBests(userId) {
    const { data } = await supabase.from('personal_bests').select('*').eq('user_id', userId).order('created_at', { ascending: true });
    setPersonalBests(data || []);
  }

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let active = true;
    supabase.from('custom_species').select('name').order('name').then(({ data }) => {
      if (active) setCustomSpecies((data || []).map((row) => row.name));
    });
    return () => { active = false; };
  }, []);

  // Adds a newly typed species to the shared list so it shows up as a suggestion for
  // everyone else too, instead of staying a one-off value only this catch/best used.
  async function registerSpecies(name) {
    const trimmed = name?.trim();
    if (!trimmed || !isSupabaseConfigured) return;
    if (KNOWN_SPECIES.has(trimmed.toLowerCase())) return;
    if (customSpecies.some((existing) => existing.toLowerCase() === trimmed.toLowerCase())) return;
    const { error } = await supabase.from('custom_species').upsert({ name: trimmed }, { onConflict: 'name', ignoreDuplicates: true });
    if (!error) setCustomSpecies((previous) => [...previous, trimmed].sort((a, b) => a.localeCompare(b)));
  }

  useEffect(() => {
    let mounted = true;
    async function loadProfile(currentUser) {
      const { data } = await supabase.from('profiles').select('*').eq('id', currentUser.id).maybeSingle();
      if (data) {
        // A later refetch (e.g. after a token refresh) can race the completeTour() write and
        // read the row before it lands, coming back with tour_completed_at still null. Once
        // this client has seen it set, keep treating the tour as done rather than letting a
        // stale read revive it.
        if (mounted) setProfile((previous) => ({ ...defaultProfile, ...data, tour_completed_at: data.tour_completed_at || previous.tour_completed_at || null }));
      } else {
        // No profiles row yet (never saved Settings) — anyone in this state can still post
        // catches and personal bests since those only need the auth user id, but the
        // Anglers directory is built from public.profiles, so they'd otherwise be invisible
        // there forever. Persist a row now so every signed-in user shows up.
        const fallback = profileFromUser(currentUser);
        if (mounted) setProfile(fallback);
        await supabase.from('profiles').upsert({ id: currentUser.id, ...fallback, updated_at: new Date().toISOString() });
      }
      if (mounted) await loadPersonalBests(currentUser.id);
    }
    async function loadSession() {
      if (!isSupabaseConfigured) {
        if (mounted) setLoading(false);
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (mounted) {
        setUser(data.session?.user || null);
        if (data.session?.user) await loadProfile(data.session.user);
        setLoading(false);
      }
    }
    loadSession();
    if (!isSupabaseConfigured) return () => { mounted = false; };
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      setUser(session?.user || null);
      if (session?.user) await loadProfile(session.user);
      else { setProfile(defaultProfile); setPersonalBests([]); }
    });
    return () => { mounted = false; listener.subscription.unsubscribe(); };
  }, []);

  async function signIn(email, password) {
    if (!isSupabaseConfigured) {
      const error = new Error('Authentication is not configured for this deployment.');
      setNotice(error.message);
      return { error };
    }
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) setNotice(result.error.message);
    return result;
  }

  async function signUp(email, password, displayName) {
    if (!isSupabaseConfigured) {
      const error = new Error('Authentication is not configured for this deployment.');
      setNotice(error.message);
      return { error };
    }
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    if (result.error) setNotice(result.error.message);
    else if (!result.data.session) setNotice('Confirmation email sent. Open it on this device, then return here to sign in.');
    return result;
  }

  async function signOut() { if (isSupabaseConfigured) await supabase.auth.signOut(); setUser(null); setProfile(defaultProfile); }

  async function updateProfile(nextProfile) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Authentication is not configured for this deployment.') };
    setProfile(nextProfile);
    const { error } = await supabase.from('profiles').upsert({ id: user.id, ...nextProfile, updated_at: new Date().toISOString() });
    if (error) setNotice(error.message);
    else if (nextProfile.favorite_species) registerSpecies(nextProfile.favorite_species);
    return { error };
  }

  // The feature tour runs once per person. The profile row is the source of truth so it
  // doesn't replay on a second device, but the timestamp is mirrored into localStorage so a
  // returning user never gets a flash of the tour in the moment before their profile loads.
  async function completeTour() {
    try { window.localStorage.setItem(TOUR_STORAGE_KEY, '1'); } catch (storageError) { /* private mode */ }
    setProfile((previous) => ({ ...previous, tour_completed_at: new Date().toISOString() }));
    if (!isSupabaseConfigured || !user) return { error: null };
    const { error } = await supabase.from('profiles').update({ tour_completed_at: new Date().toISOString() }).eq('id', user.id);
    return { error: error || null };
  }

  async function uploadAvatar(rawFile) {
    if (!rawFile?.type.startsWith('image/')) return { error: new Error('Choose an image file.') };
    const file = await compressImage(rawFile);
    if (file.size > 5 * 1024 * 1024) return { error: new Error('Profile photos must be smaller than 5 MB.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before uploading a profile photo.') };
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `${user.id}/avatar.${extension}`;
    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
    if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    const avatarUrl = `${data.publicUrl}?v=${Date.now()}`;
    const result = await updateProfile({ ...profile, avatar_url: avatarUrl });
    if (!result.error) setNotice('Profile photo updated.');
    return { ...result, avatarUrl };
  }

  async function uploadPersonalBest({ species, sizeLabel, caughtAt, file: rawFile }) {
    if (!species?.trim()) return { error: new Error('Name the species you caught.') };
    if (rawFile && !rawFile.type.startsWith('image/')) return { error: new Error('Choose an image file.') };
    const file = rawFile && await compressImage(rawFile);
    if (file && file.size > 5 * 1024 * 1024) return { error: new Error('Catch photos must be smaller than 5 MB.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before logging a personal best.') };

    let photoUrl;
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const slug = species.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const path = `${user.id}/${slug}-${Date.now()}.${extension}`;
      const { error: uploadError } = await supabase.storage.from('personal-bests').upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
      const { data } = supabase.storage.from('personal-bests').getPublicUrl(path);
      photoUrl = `${data.publicUrl}?v=${Date.now()}`;
    }

    const existing = personalBests.find((best) => best.species.toLowerCase() === species.trim().toLowerCase());
    const row = { user_id: user.id, species: species.trim(), size_label: sizeLabel || '', caught_at: caughtAt || null, ...(photoUrl ? { photo_url: photoUrl } : {}) };
    const { data, error } = existing
      ? await supabase.from('personal_bests').update(row).eq('id', existing.id).select().maybeSingle()
      : await supabase.from('personal_bests').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    setPersonalBests((previous) => existing ? previous.map((best) => (best.id === existing.id ? data : best)) : [...previous, data]);
    setNotice('Personal best saved.');
    registerSpecies(species);
    return { error: null, bestEntry: data };
  }

  async function deletePersonalBest(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a personal best.') };
    const { error } = await supabase.from('personal_bests').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    setPersonalBests((previous) => previous.filter((best) => best.id !== id));
    return { error: null };
  }

  async function listAnglers() {
    if (!isSupabaseConfigured) return [];
    const [{ data: profiles }, { data: bests }] = await Promise.all([
      supabase.from('profiles').select('*'),
      supabase.from('personal_bests').select('*'),
    ]);
    return (profiles || []).map((anglerProfile) => ({
      profile: anglerProfile,
      personalBests: (bests || []).filter((best) => best.user_id === anglerProfile.id),
    }));
  }

  async function listComments(personalBestId) {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('personal_best_comments').select('*').eq('personal_best_id', personalBestId).order('created_at', { ascending: true });
    return data || [];
  }

  async function addComment(personalBestId, body, ownerId) {
    if (!body?.trim()) return { error: new Error('Say something first.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before commenting.') };
    const authorName = profile.display_name || user?.email?.split('@')[0] || 'Angler';
    const trimmedBody = body.trim();
    const row = { personal_best_id: personalBestId, user_id: user.id, author_name: authorName, body: trimmedBody };
    const { data, error } = await supabase.from('personal_best_comments').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    notifyIfNeeded({ recipientId: ownerId, type: 'comment', targetType: 'personal_best', targetId: personalBestId, preview: trimmedBody, commentId: data.id });
    return { error: null, comment: data };
  }

  async function deleteComment(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a comment.') };
    const { error } = await supabase.from('personal_best_comments').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  async function listFishYearComments(catchId) {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('fish_year_catch_comments').select('*').eq('catch_id', catchId).order('created_at', { ascending: true });
    return data || [];
  }

  async function addFishYearComment(catchId, body, ownerId) {
    if (!body?.trim()) return { error: new Error('Say something first.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before commenting.') };
    const authorName = profile.display_name || user?.email?.split('@')[0] || 'Angler';
    const trimmedBody = body.trim();
    const row = { catch_id: catchId, user_id: user.id, author_name: authorName, body: trimmedBody };
    const { data, error } = await supabase.from('fish_year_catch_comments').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    notifyIfNeeded({ recipientId: ownerId, type: 'comment', targetType: 'fish_year_catch', targetId: catchId, preview: trimmedBody, commentId: data.id });
    return { error: null, comment: data };
  }

  async function deleteFishYearComment(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a comment.') };
    const { error } = await supabase.from('fish_year_catch_comments').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  async function listLikes(targetType, targetId) {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from(LIKE_TABLES[targetType]).select('user_id').eq(LIKE_COLUMNS[targetType], targetId);
    return data || [];
  }

  async function likeTarget(targetType, targetId, ownerId) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before liking a post.') };
    const row = { [LIKE_COLUMNS[targetType]]: targetId, user_id: user.id };
    const { error } = await supabase.from(LIKE_TABLES[targetType]).insert(row);
    if (error) { setNotice(error.message); return { error }; }
    notifyIfNeeded({ recipientId: ownerId, type: 'like', targetType, targetId, preview: '' });
    return { error: null };
  }

  async function unlikeTarget(targetType, targetId) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before unliking a post.') };
    const { error } = await supabase.from(LIKE_TABLES[targetType]).delete().eq(LIKE_COLUMNS[targetType], targetId).eq('user_id', user.id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  // Fires right after a like/comment succeeds so the recipient sees it in their bell —
  // fire-and-forget (not awaited by callers) since a failed notification insert shouldn't
  // block or error out the like/comment action that triggered it. Never notifies yourself:
  // the DB's own insert policy (actor_id <> recipient_id) would reject it anyway, but
  // checking here avoids a pointless round trip when you like or comment on your own post.
  async function notifyIfNeeded({ recipientId, type, targetType, targetId, preview, commentId }) {
    if (!isSupabaseConfigured || !user || !recipientId || recipientId === user.id) return;
    const actorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    await supabase.from('notifications').insert({
      recipient_id: recipientId,
      actor_id: user.id,
      actor_name: actorName,
      type,
      target_type: targetType,
      target_id: targetId,
      comment_id: commentId || null,
      preview: preview || '',
    });
  }

  async function listNotifications() {
    if (!isSupabaseConfigured || !user) return [];
    const { data } = await supabase.from('notifications').select('*').eq('recipient_id', user.id).order('created_at', { ascending: false }).limit(50);
    return data || [];
  }

  async function markNotificationRead(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('notifications').update({ read: true }).eq('id', id);
    return { error: error || null };
  }

  async function markAllNotificationsRead() {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('notifications').update({ read: true }).eq('recipient_id', user.id).eq('read', false);
    return { error: error || null };
  }

  async function listFishYearCatches(year) {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('fish_year_catches').select('*').eq('year', year).order('created_at', { ascending: true });
    return data || [];
  }

  async function logFishYearCatch({ year, month, species, caughtAt, file: rawFile }) {
    const trimmedSpecies = species?.trim();
    if (!trimmedSpecies) return { error: new Error('Name the species you caught.') };
    if (rawFile && !rawFile.type.startsWith('image/')) return { error: new Error('Choose an image file.') };
    const file = rawFile && await compressImage(rawFile);
    if (file && file.size > 5 * 1024 * 1024) return { error: new Error('Catch photos must be smaller than 5 MB.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before logging a catch.') };

    // Catches the accidental double-submit (a slow upload gets re-tapped, or the form is
    // reopened and filled out again) rather than a deliberate second fish of the same
    // species — same angler, same species, same day already on the board reads as one
    // catch entered twice. A transient failure here just skips the check, not the catch.
    if (caughtAt) {
      const { data: existing } = await supabase.from('fish_year_catches').select('id').eq('user_id', user.id).eq('year', year).eq('caught_at', caughtAt).ilike('species', trimmedSpecies).maybeSingle();
      if (existing) return { error: new Error(`You already logged a ${trimmedSpecies} on ${caughtAt} — that catch's already on the board.`) };
    }

    let photoUrl = '';
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const slug = trimmedSpecies.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const path = `${user.id}/${slug}-${Date.now()}.${extension}`;
      const { error: uploadError } = await supabase.storage.from('fish-year-catches').upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
      const { data } = supabase.storage.from('fish-year-catches').getPublicUrl(path);
      photoUrl = `${data.publicUrl}?v=${Date.now()}`;
    }

    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const row = { user_id: user.id, angler_name: authorName, angler_avatar_url: profile.avatar_url || '', year, month, species: trimmedSpecies, caught_at: caughtAt || null, photo_url: photoUrl };
    const { data, error } = await supabase.from('fish_year_catches').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    registerSpecies(species);
    return { error: null, catchEntry: data };
  }

  async function deleteFishYearCatch(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a catch.') };
    const { error } = await supabase.from('fish_year_catches').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  async function listTournaments() {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('tournaments').select('*').order('starts_on', { ascending: false });
    return data || [];
  }

  async function getTournament(id) {
    if (!isSupabaseConfigured) return null;
    const { data } = await supabase.from('tournaments').select('*').eq('id', id).maybeSingle();
    return data;
  }

  async function createTournament({ name, rules, unit, startsOn, endsOn }) {
    if (!name?.trim()) return { error: new Error('Name the tournament.') };
    if (!startsOn || !endsOn) return { error: new Error('Set both a start and end date.') };
    if (endsOn < startsOn) return { error: new Error('The end date has to be on or after the start date.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before starting a tournament.') };
    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const row = { name: name.trim(), rules: rules?.trim() || '', unit: unit === 'lb' ? 'lb' : 'in', starts_on: startsOn, ends_on: endsOn, created_by: user.id, created_by_name: authorName };
    const { data, error } = await supabase.from('tournaments').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, tournament: data };
  }

  async function deleteTournament(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a tournament.') };
    const { error } = await supabase.from('tournaments').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  async function listTournamentEntries(tournamentId) {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('tournament_entries').select('*').eq('tournament_id', tournamentId).order('size', { ascending: false });
    return data || [];
  }

  async function getTournamentEntry(id) {
    if (!isSupabaseConfigured) return null;
    const { data } = await supabase.from('tournament_entries').select('*').eq('id', id).maybeSingle();
    return data;
  }

  async function submitTournamentEntry({ tournamentId, species, size, caughtAt, file: rawFile }) {
    if (!species?.trim()) return { error: new Error('Name the species you caught.') };
    const numericSize = Number(size);
    if (!size || Number.isNaN(numericSize) || numericSize <= 0) return { error: new Error('Enter the size of your catch.') };
    if (rawFile && !rawFile.type.startsWith('image/')) return { error: new Error('Choose an image file.') };
    const file = rawFile && await compressImage(rawFile);
    if (file && file.size > 5 * 1024 * 1024) return { error: new Error('Catch photos must be smaller than 5 MB.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before logging a tournament entry.') };

    let photoUrl = '';
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const slug = species.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const path = `${user.id}/${slug}-${Date.now()}.${extension}`;
      const { error: uploadError } = await supabase.storage.from('tournament-entries').upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
      const { data } = supabase.storage.from('tournament-entries').getPublicUrl(path);
      photoUrl = `${data.publicUrl}?v=${Date.now()}`;
    }

    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const row = { tournament_id: tournamentId, user_id: user.id, angler_name: authorName, angler_avatar_url: profile.avatar_url || '', species: species.trim(), size: numericSize, caught_at: caughtAt || null, photo_url: photoUrl };
    const { data, error } = await supabase.from('tournament_entries').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    registerSpecies(species);
    return { error: null, entry: data };
  }

  async function deleteTournamentEntry(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a tournament entry.') };
    const { error } = await supabase.from('tournament_entries').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  async function listTournamentEntryComments(entryId) {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('tournament_entry_comments').select('*').eq('tournament_entry_id', entryId).order('created_at', { ascending: true });
    return data || [];
  }

  async function addTournamentEntryComment(entryId, body, ownerId) {
    if (!body?.trim()) return { error: new Error('Say something first.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before commenting.') };
    const authorName = profile.display_name || user?.email?.split('@')[0] || 'Angler';
    const trimmedBody = body.trim();
    const row = { tournament_entry_id: entryId, user_id: user.id, author_name: authorName, body: trimmedBody };
    const { data, error } = await supabase.from('tournament_entry_comments').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    notifyIfNeeded({ recipientId: ownerId, type: 'comment', targetType: 'tournament_entry', targetId: entryId, preview: trimmedBody, commentId: data.id });
    return { error: null, comment: data };
  }

  async function deleteTournamentEntryComment(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before removing a comment.') };
    const { error } = await supabase.from('tournament_entry_comments').delete().eq('id', id);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null };
  }

  const GAME_DEFAULT_PROFILE = { tackle_points: 0, rod_level: 1, line_level: 1, reel_level: 1, bait_level: 1, owned_lures: [], fly_rod: false, records: {}, quests: {}, bounties_claimed: [], derby_wins: [], look: {}, wardrobe: [], rebuilds: {}, charter_member: false, tags: [], junk_found: [], weekly: {}, bounty_stamps: 0 };
  const FISH_YEAR_BOUNTY_POINTS = 15;

  // Cast & Catch's tackle profile: spendable points plus gear levels. Fetch-on-demand, same
  // as everything else here — the minigame page loads it itself rather than this provider
  // holding it in global state. No row yet just means a brand-new player.
  async function getGameProfile() {
    if (!isSupabaseConfigured || !user) return null;
    const { data } = await supabase.from('game_profiles').select('*').eq('user_id', user.id).maybeSingle();
    if (data) return data;
    const fallback = { user_id: user.id, ...GAME_DEFAULT_PROFILE };
    await supabase.from('game_profiles').upsert({ ...fallback, updated_at: new Date().toISOString() });
    return fallback;
  }

  // The trophy case hangs one fish per species — the biggest you've landed — and that is
  // what game_profiles.records already names, catch_id by species, so the wall is those rows
  // by id: one small query however long the catch log grows, instead of the last fifty catches.
  // The log itself keeps every catch, because the weekly derby (this week's biggest, whether
  // or not it beat your all-time best) and the legendary feed are read off it. A record whose
  // row never came back from its insert still hangs, off the record alone.
  async function listMyTrophies(records = {}) {
    if (!isSupabaseConfigured || !user) return [];
    const entries = Object.entries(records || {}).filter(([, record]) => record && typeof record === 'object');
    const ids = entries.map(([, record]) => record.catch_id).filter(Boolean);
    const { data } = ids.length ? await supabase.from('game_catches').select('*').eq('user_id', user.id).in('id', ids) : { data: [] };
    const rowById = new Map((data || []).map((row) => [row.id, row]));
    return entries
      .map(([species, record]) => rowById.get(record.catch_id) || {
        id: `record-${species}`, species, rarity: rarityOf(species), size_in: Number(record.size_in) || 0,
        size_label: sizeLabel(Number(record.size_in) || 0), points_earned: null, created_at: record.at || null,
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }

  // Logs the catch and credits its points to the tackle balance, then rolls the
  // catch into the almanac (a new species or a bigger one than before is a record) and every
  // open NPC quest, all in the same profile write. Purely a fun side game — this never
  // touches fish_year_catches or tournament_entries.
  // `tagged` is the page's roll (TAG_CHANCE in FishingGame): a tagged fish is one another
  // member caught before — the most recent catch of that species by someone else — and the
  // tag (who, when, which catch) goes on the profile's `tags` as a collection. No row for the
  // species by anyone else means no tag, however the roll went.
  async function logGameCatch({ species, rarity, sizeLabel: label, pointsEarned, sizeIn = 0, biome = '', tagged = false, period = 'day' }) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before logging a catch.') };
    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const row = { user_id: user.id, angler_name: authorName, species, rarity, size_label: label || '', points_earned: pointsEarned || 0, size_in: sizeIn || 0, biome };
    const { data, error } = await supabase.from('game_catches').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    const currentGameProfile = await getGameProfile();
    const nextPoints = (currentGameProfile?.tackle_points || 0) + (pointsEarned || 0);
    const records = { ...(currentGameProfile?.records || {}) };
    const isRecord = isNewRecord(records, species, sizeIn);
    if (isRecord) records[species] = { size_in: sizeIn, catch_id: data?.id || null, at: new Date().toISOString() };
    const { quests, completed } = advanceQuests(currentGameProfile?.quests || {}, { species, sizeIn, biome });
    const { weekly, completed: completedWeekly } = advanceWeekly(currentGameProfile?.weekly, { species, rarity, biome, period });
    let tag = null;
    let tags = currentGameProfile?.tags || [];
    if (tagged) {
      const { data: earlier } = await supabase.from('game_catches').select('id, user_id, angler_name, created_at').eq('species', species).neq('user_id', user.id).order('created_at', { ascending: false }).limit(1);
      const theirs = earlier?.[0];
      if (theirs) {
        tag = { species, from: theirs.angler_name || 'A club member', from_id: theirs.user_id, at: theirs.created_at, catch_id: theirs.id, found_at: new Date().toISOString() };
        tags = [...tags, tag];
      }
    }
    const { data: updatedProfile, error: profileError } = await supabase.from('game_profiles')
      .update({ tackle_points: nextPoints, records, quests, tags, weekly, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (profileError) return { error: null, catchEntry: data, gameProfile: currentGameProfile, isRecord, completedQuests: completed, completedWeekly, tag };
    return { error: null, catchEntry: data, gameProfile: updatedProfile, isRecord, completedQuests: completed, completedWeekly, tag };
  }

  // Flotsam landed goes on the shelf (junk_found), once each; nothing else is written.
  async function logJunk(species) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    if (!JUNK_ROSTER.includes(species)) return { error: new Error('Not flotsam.') };
    const currentGameProfile = await getGameProfile();
    const found = currentGameProfile?.junk_found || [];
    if (found.includes(species)) return { error: null, gameProfile: currentGameProfile, isNew: false };
    const { data, error } = await supabase.from('game_profiles')
      .update({ junk_found: [...found, species], updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data, isNew: true };
  }

  // Turns in a finished quest with its giver for the points it promised. Unlock rewards
  // (The Canyon) need no turn-in — they're live the moment the quest is done.
  async function claimQuestReward(questKey) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before turning in a quest.') };
    const quest = QUEST_BY_KEY[questKey];
    if (!quest?.reward?.points) return { error: new Error('Nothing to turn in for that.') };
    const currentGameProfile = await getGameProfile();
    const state = questState(currentGameProfile?.quests, questKey);
    if (!state.done) return { error: new Error("That one's not finished yet.") };
    if (state.claimed) return { error: new Error('Already turned in.') };
    const quests = { ...(currentGameProfile?.quests || {}), [questKey]: { ...state, claimed: true } };
    const nextPoints = (currentGameProfile?.tackle_points || 0) + quest.reward.points;
    const { data, error } = await supabase.from('game_profiles')
      .update({ quests, tackle_points: nextPoints, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data, points: quest.reward.points };
  }

  // This week's club derby: everyone's catches of the target species since Monday, ranked
  // one row per angler (see utils/gameDerby.js). game_catches snapshots angler_name; avatars
  // come from profiles so the board looks like the rest of the site's leaderboards.
  async function listDerbyLeaders({ species, since, until = null }) {
    if (!isSupabaseConfigured) return [];
    let query = supabase.from('game_catches').select('*').eq('species', species).gte('created_at', since);
    if (until) query = query.lt('created_at', until);
    const [catchesRes, profilesRes] = await Promise.all([
      query.order('size_in', { ascending: false }).limit(200),
      supabase.from('profiles').select('id, display_name, avatar_url'),
    ]);
    const profileById = new Map((profilesRes.data || []).map((row) => [row.id, row]));
    const rows = (catchesRes.data || []).map((row) => ({ ...row, avatar_url: profileById.get(row.user_id)?.avatar_url || '' }));
    return rankDerby(rows);
  }

  // The derby's prize. Checks last week's final board; if you topped it and haven't been paid
  // for that week yet, the week key goes on your derby_wins and the Golden Pennant is yours
  // for this week (utils/gameDerby.isChampion). Only ever writes your own row.
  async function claimDerbyWin(now = new Date()) {
    if (!isSupabaseConfigured || !user) return { won: false };
    const last = previousDerby(now);
    const currentGameProfile = await getGameProfile();
    const wins = currentGameProfile?.derby_wins || [];
    if (wins.includes(last.key)) return { won: false, alreadyClaimed: true, derby: last };
    const leaders = await listDerbyLeaders({ species: last.species, since: last.since, until: last.until });
    const winner = leaders[0];
    if (!winner || winner.userId !== user.id) return { won: false, derby: last, winner: winner || null };
    const { data, error } = await supabase.from('game_profiles')
      .update({ derby_wins: [...wins, last.key], updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { won: false, error }; }
    return { won: true, derby: last, sizeIn: winner.sizeIn, gameProfile: data };
  }

  // Real Fish Year catches pay a tackle-point bounty in the game, once each. The angler in
  // Cast & Catch is the real person, so the real logbook is worth something at Sal's.
  async function listFishYearBounties() {
    if (!isSupabaseConfigured || !user) return [];
    const [catchesRes, currentGameProfile] = await Promise.all([
      supabase.from('fish_year_catches').select('id, species, month, year, created_at').eq('user_id', user.id).order('created_at', { ascending: false }),
      getGameProfile(),
    ]);
    const claimed = new Set(currentGameProfile?.bounties_claimed || []);
    return (catchesRes.data || []).filter((row) => !claimed.has(row.id)).map((row) => ({ id: row.id, species: row.species, month: row.month, year: row.year, points: FISH_YEAR_BOUNTY_POINTS }));
  }

  async function claimFishYearBounties() {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before claiming a bounty.') };
    const unclaimed = await listFishYearBounties();
    if (unclaimed.length === 0) return { error: new Error('Nothing new to claim — log a real catch first.') };
    const currentGameProfile = await getGameProfile();
    const points = unclaimed.length * FISH_YEAR_BOUNTY_POINTS;
    const { data, error } = await supabase.from('game_profiles').update({
      tackle_points: (currentGameProfile?.tackle_points || 0) + points,
      bounties_claimed: [...(currentGameProfile?.bounties_claimed || []), ...unclaimed.map((row) => row.id)],
      updated_at: new Date().toISOString(),
    }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data, claimed: unclaimed.length, points };
  }

  // Spends tackle points to bump one gear track a level. Every track only smooths the
  // existing cast/hookset/reel-in skill checks (see utils/gameUpgrades.js) — it never
  // auto-lands a fish for you.
  async function purchaseUpgrade(trackKey) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before upgrading your gear.') };
    const track = UPGRADE_TRACKS.find((candidate) => candidate.key === trackKey);
    if (!track) return { error: new Error('Unknown upgrade.') };
    const currentGameProfile = await getGameProfile();
    const currentLevel = currentGameProfile?.[track.column] || 1;
    if (currentLevel >= MAX_UPGRADE_LEVEL) return { error: new Error('That gear is already maxed out.') };
    const cost = upgradeCost(currentLevel);
    if ((currentGameProfile?.tackle_points || 0) < cost) return { error: new Error('Not enough tackle points yet.') };
    const nextPoints = currentGameProfile.tackle_points - cost;
    const { data, error } = await supabase.from('game_profiles')
      .update({ [track.column]: currentLevel + 1, tackle_points: nextPoints, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data };
  }

  // Turns in one of this week's bounties (utils/gameWeekly.js) for its points and a stamp.
  async function claimWeeklyBounty(slot) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before turning in a bounty.') };
    const bounty = weeklyFor().bounties.find((candidate) => candidate.slot === slot);
    if (!bounty) return { error: new Error("That's not on this week's board.") };
    const currentGameProfile = await getGameProfile();
    const status = bountyStatus(bounty, currentGameProfile?.weekly);
    if (!status.done) return { error: new Error("That one's not finished yet.") };
    if (status.claimed) return { error: new Error('Already turned in.') };
    const state = weeklyState(currentGameProfile?.weekly);
    const weekly = { ...state, claimed: [...state.claimed, slot] };
    const { data, error } = await supabase.from('game_profiles')
      .update({ weekly, tackle_points: (currentGameProfile?.tackle_points || 0) + bounty.points, bounty_stamps: (currentGameProfile?.bounty_stamps || 0) + 1, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data, points: bounty.points };
  }

  // The club records board: everyone's biggest of every species (utils/gameWeekly.js
  // clubRecords), from the catch table's biggest rows.
  async function listClubRecords() {
    if (!isSupabaseConfigured) return [];
    const { data } = await supabase.from('game_catches').select('id, species, size_in, angler_name, user_id, created_at').order('size_in', { ascending: false }).limit(1500);
    return clubRecords(data || []);
  }

  // Rebuilds a maxed track: back to level 1 for a permanent bonus (utils/gameUpgrades.js).
  async function rebuildTrack(trackKey) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before rebuilding your gear.') };
    const track = UPGRADE_TRACKS.find((candidate) => candidate.key === trackKey);
    if (!track) return { error: new Error('Unknown upgrade.') };
    const currentGameProfile = await getGameProfile();
    if ((currentGameProfile?.[track.column] || 1) < MAX_UPGRADE_LEVEL) return { error: new Error('Max it out first — a rebuild starts from the top.') };
    const rebuilds = { ...(currentGameProfile?.rebuilds || {}) };
    const done = rebuilds[trackKey] || 0;
    if (done >= MAX_REBUILDS) return { error: new Error("That one's been rebuilt as far as it goes.") };
    const cost = rebuildCost(done);
    if ((currentGameProfile?.tackle_points || 0) < cost) return { error: new Error('Not enough tackle points yet.') };
    rebuilds[trackKey] = done + 1;
    const { data, error } = await supabase.from('game_profiles')
      .update({ [track.column]: 1, rebuilds, tackle_points: currentGameProfile.tackle_points - cost, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data, rebuilds: done + 1 };
  }

  // The charter club: one purchase, every charter free from then on.
  async function joinCharterClub() {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before joining the club.') };
    const currentGameProfile = await getGameProfile();
    if (currentGameProfile?.charter_member) return { error: new Error("You're already a member.") };
    if ((currentGameProfile?.tackle_points || 0) < CHARTER_CLUB_COST) return { error: new Error('Not enough tackle points yet.') };
    const { data, error } = await supabase.from('game_profiles')
      .update({ charter_member: true, tackle_points: currentGameProfile.tackle_points - CHARTER_CLUB_COST, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data };
  }

  // Spends tackle points to charter a boat for an offshore trip — the epic/legendary species
  // only spawn out there (see utils/gameBiomes.js). One charter covers however many casts the
  // trip lasts; FishingGame only calls this again once the player has left and come back.
  async function charterBoat(biome = 'offshore') {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before chartering a boat.') };
    const currentGameProfile = await getGameProfile();
    const cost = charterFare(biome, currentGameProfile?.records || {}, { member: Boolean(currentGameProfile?.charter_member) });
    if (cost === 0) return { error: null, gameProfile: currentGameProfile, free: true };
    if ((currentGameProfile?.tackle_points || 0) < cost) return { error: new Error('Not enough tackle points to charter a boat.') };
    const nextPoints = currentGameProfile.tackle_points - cost;
    const { data, error } = await supabase.from('game_profiles')
      .update({ tackle_points: nextPoints, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data };
  }

  // One-time lure unlock, paid in tackle points. Live bait is free and never needs unlocking;
  // which lure is currently tied on is a per-session choice FishingGame keeps to itself.
  async function purchaseLure(lureKey) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before buying a lure.') };
    const lure = LURES[lureKey];
    if (!lure) return { error: new Error('Unknown lure.') };
    const currentGameProfile = await getGameProfile();
    const owned = currentGameProfile?.owned_lures || [];
    if (lure.cost === 0 || owned.includes(lureKey)) return { error: new Error('You already have that lure.') };
    if (lure.rod === 'fly' && !currentGameProfile?.fly_rod) return { error: new Error('You need the fly rod before you can fish flies.') };
    if ((currentGameProfile?.tackle_points || 0) < lure.cost) return { error: new Error('Not enough tackle points yet.') };
    const nextPoints = currentGameProfile.tackle_points - lure.cost;
    const { data, error } = await supabase.from('game_profiles')
      .update({ owned_lures: [...owned, lureKey], tackle_points: nextPoints, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data };
  }

  // The fly rod, once: a flag on the profile (migration 0021) that lets flies be bought and
  // tied on. Flies themselves go through purchaseLure like any other lure.
  async function purchaseFlyRod() {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before buying a rod.') };
    const currentGameProfile = await getGameProfile();
    if (currentGameProfile?.fly_rod) return { error: new Error('You already own the fly rod.') };
    if ((currentGameProfile?.tackle_points || 0) < FLY_ROD.cost) return { error: new Error('Not enough tackle points yet.') };
    const nextPoints = currentGameProfile.tackle_points - FLY_ROD.cost;
    const { data, error } = await supabase.from('game_profiles')
      .update({ fly_rod: true, tackle_points: nextPoints, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data };
  }

  // Marina's Outfitters: an apparel item is bought once with tackle points (wardrobe on the
  // profile); the look is what's worn, validated against what's owned before it's saved.
  async function purchaseApparel(itemKey) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before shopping.') };
    const item = WARDROBE[itemKey];
    if (!item) return { error: new Error('Unknown item.') };
    const currentGameProfile = await getGameProfile();
    const owned = currentGameProfile?.wardrobe || [];
    if (item.cost === 0 || owned.includes(itemKey)) return { error: new Error('You already own that.') };
    if ((currentGameProfile?.tackle_points || 0) < item.cost) return { error: new Error('Not enough tackle points yet.') };
    const nextPoints = currentGameProfile.tackle_points - item.cost;
    const { data, error } = await supabase.from('game_profiles')
      .update({ wardrobe: [...owned, itemKey], tackle_points: nextPoints, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data };
  }

  async function saveLook(look) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before changing your look.') };
    const currentGameProfile = await getGameProfile();
    const safe = normalizeLook(look, currentGameProfile?.wardrobe || []);
    const { data, error } = await supabase.from('game_profiles')
      .update({ look: safe, updated_at: new Date().toISOString() }).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, gameProfile: data, look: safe };
  }

  async function submitBugReport({ body }) {
    if (!body?.trim()) return { error: new Error('Describe what went wrong first.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before reporting a bug.') };
    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const row = { user_id: user.id, angler_name: authorName, body: body.trim(), page_url: window.location.pathname };
    const { data, error } = await supabase.from('bug_reports').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, report: data };
  }

  // Only migration 0022's admin-scoped RLS policy makes this return more than the caller's own
  // reports — everyone else still only ever sees their own rows via the reporter-only policy.
  async function listBugReports() {
    if (!isSupabaseConfigured) return [];
    const { data, error } = await supabase.from('bug_reports').select('*').order('created_at', { ascending: false });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  // The fishing-licenses bucket is private (unlike every other photo bucket in this app,
  // license numbers aren't crew business), so a stored path needs a fresh signed URL on every
  // read rather than the permanent public URL the other upload* functions save straight to
  // the row — nothing durable gets cached here, it's regenerated each time.
  async function signedLicenseUrl(path) {
    if (!path) return '';
    const { data } = await supabase.storage.from('fishing-licenses').createSignedUrl(path, 3600);
    return data?.signedUrl || '';
  }

  async function listFishingLicenses() {
    if (!isSupabaseConfigured || !user) return [];
    const { data, error } = await supabase.from('fishing_licenses').select('*').eq('user_id', user.id).order('expires_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return Promise.all((data || []).map(async (license) => ({ ...license, photo_url: await signedLicenseUrl(license.photo_path) })));
  }

  // A scanned license is often a PDF, not a photo — only images go through compressImage,
  // which assumes it can decode the file as a bitmap. Shared by upload and update so a
  // renewed license is validated exactly like a freshly added one.
  async function prepareLicenseFile(rawFile) {
    if (!rawFile) return { file: null };
    const isImage = rawFile.type.startsWith('image/');
    const isPdf = rawFile.type === 'application/pdf';
    if (!isImage && !isPdf) return { error: new Error('Choose an image or PDF file.') };
    const file = isImage ? await compressImage(rawFile) : rawFile;
    if (file.size > 5 * 1024 * 1024) return { error: new Error('License files must be smaller than 5 MB.') };
    return { file };
  }

  async function uploadFishingLicense({ state, licenseNumber, issuedAt, expiresAt, file: rawFile }) {
    const trimmedState = state?.trim();
    if (!trimmedState) return { error: new Error('Choose which state this license is for.') };
    if (!expiresAt) return { error: new Error('Add the expiration date.') };
    const { file, error: fileError } = await prepareLicenseFile(rawFile);
    if (fileError) return { error: fileError };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before adding a license.') };
    let photoPath = '';
    if (file) {
      photoPath = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from('fishing-licenses').upload(photoPath, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
    }
    const row = { user_id: user.id, state: trimmedState, license_number: licenseNumber?.trim() || '', issued_at: issuedAt || null, expires_at: expiresAt, photo_path: photoPath };
    const { data, error } = await supabase.from('fishing_licenses').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, license: { ...data, photo_url: await signedLicenseUrl(data.photo_path) } };
  }

  // Renews or edits a license in place (id and previousPhotoPath describe the existing row)
  // rather than making the caller delete and re-add one — a state's new license keeps the
  // same row so its history doesn't reset, and only the fields actually filled in change.
  async function updateFishingLicense({ id, previousPhotoPath, state, licenseNumber, issuedAt, expiresAt, file: rawFile }) {
    const trimmedState = state?.trim();
    if (!trimmedState) return { error: new Error('Choose which state this license is for.') };
    if (!expiresAt) return { error: new Error('Add the expiration date.') };
    const { file, error: fileError } = await prepareLicenseFile(rawFile);
    if (fileError) return { error: fileError };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before updating a license.') };
    const updates = { state: trimmedState, license_number: licenseNumber?.trim() || '', issued_at: issuedAt || null, expires_at: expiresAt };
    if (file) {
      updates.photo_path = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from('fishing-licenses').upload(updates.photo_path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
    }
    const { data, error } = await supabase.from('fishing_licenses').update(updates).eq('id', id).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    // Only drop the old file once the new row has actually committed, so a failed update
    // never orphans the license that's still current.
    if (file && previousPhotoPath) await supabase.storage.from('fishing-licenses').remove([previousPhotoPath]);
    return { error: null, license: { ...data, photo_url: await signedLicenseUrl(data.photo_path) } };
  }

  async function deleteFishingLicense(id, photoPath) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before managing licenses.') };
    if (photoPath) await supabase.storage.from('fishing-licenses').remove([photoPath]);
    const { error } = await supabase.from('fishing_licenses').delete().eq('id', id).eq('user_id', user.id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  // Waypoint maps: private by default, visible only to their owner and anglers who have
  // *accepted* an invitation (enforced by RLS via is_waypoint_map_collaborator — a pending
  // invite grants nothing). listMyWaypointMaps only sees rows RLS lets through, so no extra
  // client-side filtering is needed on top of it.
  async function listMyWaypointMaps() {
    if (!isSupabaseConfigured || !user) return [];
    const { data: maps, error } = await supabase.from('waypoint_maps').select('*').order('created_at', { ascending: false });
    if (error) { setNotice(error.message); return []; }
    if (!maps?.length) return [];
    const mapIds = maps.map((map) => map.id);
    const [{ data: waypoints }, { data: members }] = await Promise.all([
      supabase.from('waypoints').select('id, map_id').in('map_id', mapIds),
      supabase.from('waypoint_map_members').select('map_id, status').in('map_id', mapIds),
    ]);
    return maps.map((map) => ({
      ...map,
      isOwner: map.owner_id === user.id,
      waypointCount: (waypoints || []).filter((w) => w.map_id === map.id).length,
      memberCount: (members || []).filter((m) => m.map_id === map.id && m.status === 'accepted').length,
    }));
  }

  async function getWaypointMap(id) {
    if (!isSupabaseConfigured || !user) return null;
    const { data } = await supabase.from('waypoint_maps').select('*').eq('id', id).maybeSingle();
    if (!data) return null;
    return { ...data, isOwner: data.owner_id === user.id };
  }

  async function createWaypointMap({ name, description }) {
    const trimmedName = name?.trim();
    if (!trimmedName) return { error: new Error('Name the map first.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before creating a map.') };
    const row = { owner_id: user.id, name: trimmedName, description: description?.trim() || '' };
    const { data, error } = await supabase.from('waypoint_maps').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, map: { ...data, isOwner: true, waypointCount: 0, memberCount: 0 } };
  }

  async function deleteWaypointMap(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('waypoint_maps').delete().eq('id', id).eq('owner_id', user.id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  // profiles has an open "select using (true)" policy (it's how the Anglers directory works),
  // so joining against it here is safe even for a collaborator who can't otherwise see much
  // about the other members yet.
  async function listMapMembers(mapId) {
    if (!isSupabaseConfigured) return [];
    const { data: members, error } = await supabase.from('waypoint_map_members').select('*').eq('map_id', mapId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    if (!members?.length) return [];
    const { data: profiles } = await supabase.from('profiles').select('id, display_name, avatar_url').in('id', members.map((member) => member.user_id));
    return members.map((member) => ({ ...member, profile: (profiles || []).find((profile) => profile.id === member.user_id) || null }));
  }

  async function inviteToWaypointMap(mapId, mapName, userId) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before inviting anyone.') };
    const row = { map_id: mapId, map_name: mapName, user_id: userId, invited_by: user.id, status: 'pending' };
    const { data, error } = await supabase.from('waypoint_map_members').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    // Never .select()ed: only the recipient can read a notification.
    supabase.from('notifications').insert({ recipient_id: userId, actor_id: user.id, actor_name: myName(), type: 'map_invite', target_type: 'waypoint_map', target_id: mapId, preview: mapName || '' }).then(() => {});
    return { error: null, member: data };
  }

  async function removeMapMember(memberId) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('waypoint_map_members').delete().eq('id', memberId);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  // Declining an invite just removes the row (it's the invitee's own row, per RLS) rather
  // than tracking a third status — there's nothing useful to keep once it's turned down.
  async function respondToWaypointInvite(memberId, accept) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    if (!accept) {
      const { error } = await supabase.from('waypoint_map_members').delete().eq('id', memberId).eq('user_id', user.id);
      if (error) setNotice(error.message);
      return { error: error || null };
    }
    const { data, error } = await supabase.from('waypoint_map_members').update({ status: 'accepted' }).eq('id', memberId).eq('user_id', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, member: data };
  }

  async function listMyWaypointInvites() {
    if (!isSupabaseConfigured || !user) return [];
    const { data: invites, error } = await supabase.from('waypoint_map_members').select('*').eq('user_id', user.id).eq('status', 'pending').order('created_at', { ascending: false });
    if (error) { setNotice(error.message); return []; }
    if (!invites?.length) return [];
    const { data: profiles } = await supabase.from('profiles').select('id, display_name').in('id', invites.map((invite) => invite.invited_by));
    return invites.map((invite) => ({ ...invite, inviterName: (profiles || []).find((profile) => profile.id === invite.invited_by)?.display_name || 'Someone' }));
  }

  async function listWaypoints(mapId) {
    if (!isSupabaseConfigured) return [];
    const { data, error } = await supabase.from('waypoints').select('*').eq('map_id', mapId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  async function addWaypoint({ mapId, name, lat, lng, notes }) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before adding a waypoint.') };
    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const row = { map_id: mapId, created_by: user.id, created_by_name: authorName, name: name?.trim() || 'Waypoint', lat, lng, notes: notes?.trim() || '', source: 'manual' };
    const { data, error } = await supabase.from('waypoints').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, waypoint: data };
  }

  // points come pre-parsed from parseGpx/parseKml (name/lat/lng/notes) — one bulk insert
  // rather than one round trip per pin, since a chartplotter export can easily hold a few
  // hundred.
  async function importWaypoints({ mapId, points, source = 'gpx' }) {
    if (!points?.length) return { error: new Error('No waypoints to import.') };
    if (source !== 'gpx' && source !== 'kml') return { error: new Error('Unknown import source.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before importing waypoints.') };
    const authorName = profile.display_name || user.email?.split('@')[0] || 'Angler';
    const rows = points.map((point) => ({ map_id: mapId, created_by: user.id, created_by_name: authorName, name: point.name, lat: point.lat, lng: point.lng, notes: point.notes || '', source }));
    const { data, error } = await supabase.from('waypoints').insert(rows).select();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, waypoints: data || [] };
  }

  async function deleteWaypoint(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('waypoints').delete().eq('id', id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  // Trips (migration 0028): the crew sees every trip and anyone can opt in; the creator edits
  // it and can remove people; expenses and payments are visible to people on the trip only.
  function myName() {
    return profile.display_name || user.email?.split('@')[0] || 'Angler';
  }

  // Validated here rather than trusted from the form: the accommodation link is rendered as
  // an <a href>, so anything but http(s) (a javascript: URL, say) is refused outright.
  function tripRowFrom(fields) {
    const name = fields.name?.trim();
    if (!name) return { error: new Error('Name the trip.') };
    if (!fields.startsOn || !fields.endsOn) return { error: new Error('Set both a start and end date.') };
    if (fields.endsOn < fields.startsOn) return { error: new Error('The end date has to be on or after the start date.') };
    if (fields.rsvpBy && fields.rsvpBy > fields.endsOn) return { error: new Error('The RSVP deadline has to be on or before the trip ends.') };
    const spots = String(fields.maxSpots ?? '').trim();
    if (spots && !/^[1-9]\d*$/.test(spots)) return { error: new Error('Spots has to be a whole number, or blank for no limit.') };
    let accommodationUrl = fields.accommodationUrl?.trim() || '';
    if (accommodationUrl && !/^[a-z][a-z0-9+.-]*:/i.test(accommodationUrl)) accommodationUrl = `https://${accommodationUrl}`;
    if (accommodationUrl && !/^https?:\/\/[^\s]+$/i.test(accommodationUrl)) return { error: new Error('The accommodation link has to be a web address.') };
    return {
      error: null,
      row: {
        name,
        location: fields.location?.trim() || '',
        state: fields.state || '',
        starts_on: fields.startsOn,
        ends_on: fields.endsOn,
        target_species: (fields.targetSpecies || []).map((species) => species.trim()).filter(Boolean),
        accommodation: fields.accommodation?.trim() || '',
        accommodation_url: accommodationUrl,
        notes: fields.notes?.trim() || '',
        max_spots: spots ? Number(spots) : null,
        rsvp_by: fields.rsvpBy || null,
      },
    };
  }

  async function listTrips() {
    if (!isSupabaseConfigured || !user) return [];
    const [{ data: trips, error }, { data: attendees }] = await Promise.all([
      supabase.from('trips').select('*').order('starts_on', { ascending: true }),
      supabase.from('trip_attendees').select('id, trip_id, user_id, created_at'),
    ]);
    if (error) { setNotice(error.message); return []; }
    return (trips || []).map((trip) => ({ ...trip, attendees: (attendees || []).filter((attendee) => attendee.trip_id === trip.id) }));
  }

  async function getTrip(id) {
    if (!isSupabaseConfigured || !user) return null;
    const { data } = await supabase.from('trips').select('*').eq('id', id).maybeSingle();
    return data || null;
  }

  async function createTrip(fields) {
    const parsed = tripRowFrom(fields);
    if (parsed.error) return { error: parsed.error };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before planning a trip.') };
    const { data: trip, error } = await supabase.from('trips').insert({ ...parsed.row, created_by: user.id, created_by_name: myName() }).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    // The creator is going on their own trip; if this fails the trip still exists and they can opt in.
    await supabase.from('trip_attendees').insert({ trip_id: trip.id, user_id: user.id, angler_name: myName() });
    return { error: null, trip };
  }

  async function updateTrip(id, fields) {
    const parsed = tripRowFrom(fields);
    if (parsed.error) return { error: parsed.error };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { data, error } = await supabase.from('trips').update(parsed.row).eq('id', id).eq('created_by', user.id).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, trip: data };
  }

  async function deleteTrip(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trips').delete().eq('id', id).eq('created_by', user.id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  async function listTripAttendees(tripId) {
    if (!isSupabaseConfigured || !user) return [];
    const { data, error } = await supabase.from('trip_attendees').select('*').eq('trip_id', tripId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  // Trip notifications go to several people at once and are never read back: a notification
  // is only visible to its recipient, so an insert that returned the row would fail RLS.
  // Fire-and-forget, like notifyIfNeeded: a missed notification never undoes the action.
  async function notifyTrip({ recipientIds, type, tripId, preview }) {
    const rows = [...new Set(recipientIds || [])].filter((id) => id && id !== user.id).map((recipientId) => ({
      recipient_id: recipientId, actor_id: user.id, actor_name: myName(), type, target_type: 'trip', target_id: tripId, preview: preview || '',
    }));
    if (rows.length) await supabase.from('notifications').insert(rows);
  }

  async function joinTrip(tripId, { creatorId, tripName } = {}) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before joining a trip.') };
    const { data, error } = await supabase.from('trip_attendees').insert({ trip_id: tripId, user_id: user.id, angler_name: myName() }).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    notifyTrip({ recipientIds: [creatorId], type: 'trip_join', tripId, preview: tripName });
    return { error: null, attendee: data };
  }

  async function leaveTrip(tripId) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trip_attendees').delete().eq('trip_id', tripId).eq('user_id', user.id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  async function removeTripAttendee(attendeeId) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trip_attendees').delete().eq('id', attendeeId);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  async function listTripExpenses(tripId) {
    if (!isSupabaseConfigured || !user) return [];
    const { data, error } = await supabase.from('trip_expenses').select('*').eq('trip_id', tripId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  async function addTripExpense({ tripId, description, amountCents, notifyUserIds = [] }) {
    const trimmed = description?.trim();
    if (!trimmed) return { error: new Error('Say what the expense was for.') };
    if (!Number.isInteger(amountCents) || amountCents <= 0) return { error: new Error('Enter an amount like 85 or 85.50.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const row = { trip_id: tripId, paid_by: user.id, paid_by_name: myName(), description: trimmed, amount_cents: amountCents };
    const { data, error } = await supabase.from('trip_expenses').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    notifyTrip({ recipientIds: notifyUserIds, type: 'trip_expense', tripId, preview: `${trimmed} · $${(amountCents / 100).toFixed(2)}` });
    return { error: null, expense: data };
  }

  async function deleteTripExpense(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trip_expenses').delete().eq('id', id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  async function listTripSettlements(tripId) {
    if (!isSupabaseConfigured || !user) return [];
    const { data, error } = await supabase.from('trip_settlements').select('*').eq('trip_id', tripId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  // "from" paid "to" back; either of them can record it.
  async function recordTripSettlement({ tripId, from, to, amountCents }) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const row = { trip_id: tripId, from_user: from.userId, from_name: from.name, to_user: to.userId, to_name: to.name, amount_cents: amountCents, created_by: user.id };
    const { data, error } = await supabase.from('trip_settlements').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, settlement: data };
  }

  async function deleteTripSettlement(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trip_settlements').delete().eq('id', id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  async function listVenmoHandles(userIds) {
    if (!isSupabaseConfigured || !user || !userIds?.length) return {};
    const { data } = await supabase.from('profiles').select('id, venmo_handle').in('id', userIds);
    return Object.fromEntries((data || []).filter((row) => row.venmo_handle).map((row) => [row.id, row.venmo_handle]));
  }

  // Zelle / Apple Cash details (migration 0032): readable only by people on a trip with you.
  async function getMyPaymentContacts() {
    if (!isSupabaseConfigured || !user) return { zelle: '', apple_cash_phone: '' };
    const { data } = await supabase.from('payment_contacts').select('zelle, apple_cash_phone').eq('user_id', user.id).maybeSingle();
    return data || { zelle: '', apple_cash_phone: '' };
  }

  async function saveMyPaymentContacts({ zelle, appleCashPhone }) {
    const zelleResult = normalizeZelle(zelle);
    if (zelleResult.error) return { error: new Error(zelleResult.error) };
    const phoneResult = normalizeUsPhone(appleCashPhone);
    if (phoneResult.error) return { error: new Error(phoneResult.error) };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const row = { user_id: user.id, zelle: zelleResult.zelle, apple_cash_phone: phoneResult.phone, updated_at: new Date().toISOString() };
    const { error } = await supabase.from('payment_contacts').upsert(row);
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, contacts: { zelle: row.zelle, apple_cash_phone: row.apple_cash_phone } };
  }

  async function listPaymentContacts(userIds) {
    if (!isSupabaseConfigured || !user || !userIds?.length) return {};
    const { data } = await supabase.from('payment_contacts').select('user_id, zelle, apple_cash_phone').in('user_id', userIds);
    return Object.fromEntries((data || []).map((row) => [row.user_id, { zelle: row.zelle, appleCashPhone: row.apple_cash_phone }]));
  }

  async function listTripItems(tripId) {
    if (!isSupabaseConfigured || !user) return [];
    const { data, error } = await supabase.from('trip_items').select('*').eq('trip_id', tripId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  async function addTripItem({ tripId, name }) {
    const trimmed = name?.trim();
    if (!trimmed) return { error: new Error('Name the item.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { data, error } = await supabase.from('trip_items').insert({ trip_id: tripId, name: trimmed, created_by: user.id }).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    return { error: null, item: data };
  }

  // Claim it for yourself, or let go of one you claimed; RLS won't let you touch anyone else's claim.
  async function setTripItemClaim(itemId, claim) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const change = claim ? { claimed_by: user.id, claimed_by_name: myName() } : { claimed_by: null, claimed_by_name: '' };
    const { data, error } = await supabase.from('trip_items').update(change).eq('id', itemId).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    if (!data) return { error: new Error('Someone else already has that one.') };
    return { error: null, item: data };
  }

  async function deleteTripItem(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trip_items').delete().eq('id', id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  async function listTripCatches(tripId) {
    if (!isSupabaseConfigured || !user) return [];
    const { data, error } = await supabase.from('trip_catches').select('*').eq('trip_id', tripId).order('created_at', { ascending: true });
    if (error) { setNotice(error.message); return []; }
    return data || [];
  }

  async function logTripCatch({ tripId, species, lengthIn, caughtAt, file: rawFile }) {
    if (!species?.trim()) return { error: new Error('Name the species you caught.') };
    const length = String(lengthIn ?? '').trim();
    if (length && !(Number(length) > 0)) return { error: new Error('Length has to be a number of inches, or blank.') };
    if (rawFile && !rawFile.type.startsWith('image/')) return { error: new Error('Choose an image file.') };
    const file = rawFile && await compressImage(rawFile);
    if (file && file.size > 5 * 1024 * 1024) return { error: new Error('Catch photos must be smaller than 5 MB.') };
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in before logging a catch.') };

    let photoUrl = '';
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const slug = species.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const path = `${user.id}/${slug}-${Date.now()}.${extension}`;
      const { error: uploadError } = await supabase.storage.from('trip-catches').upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (uploadError) { setNotice(uploadError.message); return { error: uploadError }; }
      const { data } = supabase.storage.from('trip-catches').getPublicUrl(path);
      photoUrl = `${data.publicUrl}?v=${Date.now()}`;
    }

    const row = { trip_id: tripId, user_id: user.id, angler_name: myName(), species: species.trim(), length_in: length ? Number(length) : null, caught_at: caughtAt || null, photo_url: photoUrl };
    const { data, error } = await supabase.from('trip_catches').insert(row).select().maybeSingle();
    if (error) { setNotice(error.message); return { error }; }
    registerSpecies(species.trim());
    return { error: null, tripCatch: data };
  }

  async function deleteTripCatch(id) {
    if (!isSupabaseConfigured || !user) return { error: new Error('Sign in first.') };
    const { error } = await supabase.from('trip_catches').delete().eq('id', id);
    if (error) setNotice(error.message);
    return { error: error || null };
  }

  // Merges the crew's three kinds of posts into one reverse-chronological feed for the Home
  // page. personal_bests doesn't snapshot an angler_name/avatar the way the other two do, so
  // it's joined against profiles here; tournament_entries needs its parent tournament's name
  // and unit to read as more than a bare number.
  async function listRecentActivity(limit = 30) {
    if (!isSupabaseConfigured) return [];
    const [catchesRes, bestsRes, entriesRes, profilesRes, tournamentsRes, gameRes] = await Promise.all([
      supabase.from('fish_year_catches').select('*').order('created_at', { ascending: false }).limit(limit),
      supabase.from('personal_bests').select('*').order('created_at', { ascending: false }).limit(limit),
      supabase.from('tournament_entries').select('*').order('created_at', { ascending: false }).limit(limit),
      supabase.from('profiles').select('id, display_name, avatar_url'),
      supabase.from('tournaments').select('id, name, unit'),
      supabase.from('game_catches').select('*').eq('rarity', 'legendary').order('created_at', { ascending: false }).limit(limit),
    ]);
    const profileById = new Map((profilesRes.data || []).map((row) => [row.id, row]));
    const tournamentById = new Map((tournamentsRes.data || []).map((row) => [row.id, row]));

    const catches = (catchesRes.data || []).map((row) => ({
      kind: 'fish_year_catch', id: row.id, userId: row.user_id, anglerName: row.angler_name, avatarUrl: row.angler_avatar_url,
      species: row.species, photoUrl: row.photo_url, caughtAt: row.caught_at, createdAt: row.created_at,
      month: row.month, href: `/fish-year?catch=${row.id}`,
    }));
    const bests = (bestsRes.data || []).map((row) => {
      const profile = profileById.get(row.user_id);
      return {
        kind: 'personal_best', id: row.id, userId: row.user_id, anglerName: profile?.display_name || 'Angler', avatarUrl: profile?.avatar_url || '',
        species: row.species, photoUrl: row.photo_url, caughtAt: row.caught_at, createdAt: row.created_at,
        sizeLabel: row.size_label, href: `/anglers?best=${row.id}`,
      };
    });
    const entries = (entriesRes.data || []).map((row) => {
      const tournament = tournamentById.get(row.tournament_id);
      return {
        kind: 'tournament_entry', id: row.id, userId: row.user_id, anglerName: row.angler_name, avatarUrl: row.angler_avatar_url,
        species: row.species, photoUrl: row.photo_url, caughtAt: row.caught_at, createdAt: row.created_at,
        size: row.size, unit: tournament?.unit || 'in', tournamentName: tournament?.name || 'a tournament',
        href: `/tournaments/${row.tournament_id}?entry=${row.id}`,
      };
    });

    // Only legendary game catches make the feed — a club-wide "did you see that?" moment,
    // not every bluegill from the minigame.
    const gameCatches = (gameRes?.data || []).map((row) => gameCatchActivity(row, profileById.get(row.user_id)));

    return [...catches, ...bests, ...entries, ...gameCatches]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, limit);
  }

  function gameCatchActivity(row, profileRow) {
    return {
      kind: 'game_catch', id: row.id, userId: row.user_id, anglerName: row.angler_name, avatarUrl: profileRow?.avatar_url || '',
      species: speciesLabel(row.species), photoUrl: '', caughtAt: row.created_at, createdAt: row.created_at,
      sizeLabel: row.size_in ? sizeLabel(row.size_in) : row.size_label, rarity: row.rarity, href: '/fishing-game',
    };
  }

  // Gives Home's activity feed a "someone just posted" feel via Supabase Realtime instead
  // of only refreshing on the next page load. Builds each pushed item into the exact same
  // shape listRecentActivity returns, so ActivityFeed doesn't need to know the difference.
  // fish_year_catches and tournament_entries snapshot the poster's name/avatar on the row
  // itself, same as listRecentActivity relies on; personal_bests doesn't, so that one needs
  // a follow-up profile lookup, and tournament_entries needs its parent tournament's name/
  // unit the same way listRecentActivity's own query joins it in.
  function subscribeToActivity(onInsert) {
    if (!isSupabaseConfigured) return () => {};
    const channel = supabase.channel('home-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'fish_year_catches' }, ({ new: row }) => {
        onInsert({
          kind: 'fish_year_catch', id: row.id, userId: row.user_id, anglerName: row.angler_name, avatarUrl: row.angler_avatar_url,
          species: row.species, photoUrl: row.photo_url, caughtAt: row.caught_at, createdAt: row.created_at,
          month: row.month, href: `/fish-year?catch=${row.id}`,
        });
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'personal_bests' }, async ({ new: row }) => {
        const { data: profileRow } = await supabase.from('profiles').select('display_name, avatar_url').eq('id', row.user_id).maybeSingle();
        onInsert({
          kind: 'personal_best', id: row.id, userId: row.user_id, anglerName: profileRow?.display_name || 'Angler', avatarUrl: profileRow?.avatar_url || '',
          species: row.species, photoUrl: row.photo_url, caughtAt: row.caught_at, createdAt: row.created_at,
          sizeLabel: row.size_label, href: `/anglers?best=${row.id}`,
        });
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'game_catches' }, async ({ new: row }) => {
        if (row.rarity !== 'legendary') return;
        const { data: profileRow } = await supabase.from('profiles').select('display_name, avatar_url').eq('id', row.user_id).maybeSingle();
        onInsert(gameCatchActivity(row, profileRow));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tournament_entries' }, async ({ new: row }) => {
        const tournament = await getTournament(row.tournament_id);
        onInsert({
          kind: 'tournament_entry', id: row.id, userId: row.user_id, anglerName: row.angler_name, avatarUrl: row.angler_avatar_url,
          species: row.species, photoUrl: row.photo_url, caughtAt: row.caught_at, createdAt: row.created_at,
          size: row.size, unit: tournament?.unit || 'in', tournamentName: tournament?.name || 'a tournament',
          href: `/tournaments/${row.tournament_id}?entry=${row.id}`,
        });
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }

  // Cast & Catch presence: everyone with the game open shares one Realtime presence channel,
  // keyed by user id, carrying where they are and what they're doing. Nothing is stored —
  // Presence is in-memory on the Realtime server and vanishes when the tab closes — so this is
  // the one piece of the game that is deliberately not a table. onSync gets everyone but you.
  //
  // The Realtime client hands back the *existing* channel for a topic, and subscribing to one
  // that is still leaving is a silent no-op (no status callback, no track — presence just goes
  // dark). A rejoin straight after a leave (StrictMode, a remount, a provider re-render) hits
  // exactly that, so a stale channel on this topic is removed and awaited before a fresh one
  // is opened; the handle queues the latest payload until then.
  const DOCK_TOPIC = 'cast-and-catch-dock';
  function joinDock(initial, onSync) {
    if (!isSupabaseConfigured || !user) return { update: () => {}, leave: () => {} };
    let channel = null;
    let latest = initial;
    let left = false;
    const sync = () => {
      if (!channel) return;
      const state = channel.presenceState() || {};
      const others = Object.entries(state)
        .filter(([key]) => key !== user.id)
        .map(([key, metas]) => ({ userId: key, ...(metas[metas.length - 1] || {}) }))
        .filter((entry) => entry.name);
      onSync(others);
    };
    const open = () => {
      if (left) return;
      channel = supabase.channel(DOCK_TOPIC, { config: { presence: { key: user.id } } });
      channel.on('presence', { event: 'sync' }, sync).subscribe(async (status) => {
        // Fires again after a reconnect, so what we're doing is re-announced then too.
        if (status === 'SUBSCRIBED' && channel) await channel.track({ ...latest, at: Date.now() });
      });
    };
    const stale = (typeof supabase.getChannels === 'function' ? supabase.getChannels() : []).filter((existing) => existing.topic === `realtime:${DOCK_TOPIC}`);
    if (stale.length === 0) open();
    else Promise.all(stale.map((existing) => supabase.removeChannel(existing))).then(open, open);
    return {
      update: (payload) => { latest = payload; if (channel) channel.track({ ...payload, at: Date.now() }); },
      leave: () => { left = true; if (channel) supabase.removeChannel(channel); channel = null; },
    };
  }

  const shouldShowTour = Boolean(user) && !loading && !profile.tour_completed_at && !tourSeenLocally();

  return <AuthContext.Provider value={{
    user, profile, personalBests, customSpecies, loading, notice, setNotice,
    shouldShowTour, completeTour,
    signIn, signUp, signOut, updateProfile, uploadAvatar,
    uploadPersonalBest, deletePersonalBest, listAnglers,
    listComments, addComment, deleteComment,
    listFishYearCatches, logFishYearCatch, deleteFishYearCatch,
    listFishYearComments, addFishYearComment, deleteFishYearComment,
    listTournaments, getTournament, createTournament, deleteTournament,
    listTournamentEntries, getTournamentEntry, submitTournamentEntry, deleteTournamentEntry,
    listTournamentEntryComments, addTournamentEntryComment, deleteTournamentEntryComment,
    subscribeToActivity,
    listRecentActivity,
    listLikes, likeTarget, unlikeTarget,
    listNotifications, markNotificationRead, markAllNotificationsRead,
    submitBugReport, listBugReports,
    listFishingLicenses, uploadFishingLicense, updateFishingLicense, deleteFishingLicense,
    listMyWaypointMaps, getWaypointMap, createWaypointMap, deleteWaypointMap,
    listMapMembers, inviteToWaypointMap, removeMapMember, respondToWaypointInvite, listMyWaypointInvites,
    listWaypoints, addWaypoint, importWaypoints, deleteWaypoint,
    listTrips, getTrip, createTrip, updateTrip, deleteTrip,
    listTripAttendees, joinTrip, leaveTrip, removeTripAttendee,
    listTripExpenses, addTripExpense, deleteTripExpense,
    listTripSettlements, recordTripSettlement, deleteTripSettlement,
    listVenmoHandles, listTripItems, addTripItem, setTripItemClaim, deleteTripItem,
    getMyPaymentContacts, saveMyPaymentContacts, listPaymentContacts,
    listTripCatches, logTripCatch, deleteTripCatch,
    getGameProfile, listMyTrophies, logGameCatch, logJunk, purchaseUpgrade, rebuildTrack, joinCharterClub, claimWeeklyBounty, listClubRecords, charterBoat, purchaseLure, purchaseFlyRod, purchaseApparel, saveLook,
    claimQuestReward, listDerbyLeaders, listFishYearBounties, claimFishYearBounties, joinDock, claimDerbyWin,
    isSupabaseConfigured,
  }}>{children}</AuthContext.Provider>;
}

export function useAuth() { return useContext(AuthContext); }
